import { Attr, Names } from '@mode-switch/model';
import { QueryResult } from '@mode-switch/query';
import { EncField, EncSeries, Encoding } from '@mode-vegas/vegas';
import { EncodingHelpers, RenderDataModel, TOTALS_TOKEN, TOTAL_LABEL, TotalSeriesData } from '@mode/charts/util-vegas';
import { ChartTypes } from '@mode/shared/contract-common';
import {
  MuzeContextInfo,
  MuzeDataField,
  MuzeDataFormatFn,
  MuzeDataItem,
  MuzeDataSchema,
  MuzeDataset,
  MuzeTextDataInfo,
  SID_FIELD,
  SeriesMarkSummary,
  buildMeasureNamesFieldMap,
  buildMuzeMeasureNamesTextFormatter,
  getSeriesMarksSummaryAt,
} from './muze-chart-data';

// Cached ref, here to pacify eslint rule
const hasOwnProp = Object.prototype.hasOwnProperty;

export type TotalMuzeData = Partial<{
  col: MuzeDataset;
  row: MuzeDataset;
  all: MuzeDataset;
}>;

export interface SeriesMarkSummaryMap {
  readonly [series: string]: SeriesMarkSummary;
}

export function makeTotalsData(model: RenderDataModel, schema: MuzeDataSchema, encoding: Encoding): TotalMuzeData {
  switch (encoding.marks.channel) {
    case '*': {
      return makeTabularTotalsData(model, schema);
    }
    default: {
      return makeSeriesTotalsData(model, schema, encoding);
    }
  }
}

export function makeTabularTotalsData(model: RenderDataModel, schema: MuzeDataSchema): TotalMuzeData {
  return makeTabularTotals(model.getTotalSeriesData('*'), schema);
}

export function makeSubtotalsData(model: RenderDataModel, schema: MuzeDataSchema, encoding: Encoding): TotalMuzeData {
  switch (encoding.marks.channel) {
    case '*': {
      const subtotalsData = model.getSubtotalSeriesData(encoding, '*');
      const tabularData = makeTabularTotals(subtotalsData, schema, true);
      return tabularData;
    }
    default: {
      const seriesData = makeSeriesSubtotalsData(model, schema, encoding);
      return seriesData;
    }
  }
}

export function makeSeriesTotalsData(
  model: RenderDataModel,
  schema: MuzeDataSchema,
  encoding: Encoding
): TotalMuzeData {
  return makeSeriesDataImpl(model, schema, encoding, (series) => model.getTotalSeriesData(series));
}

export function makeSeriesSubtotalsData(model: RenderDataModel, schema: MuzeDataSchema, encoding: Encoding) {
  return makeSeriesDataImpl(model, schema, encoding, (series) => model.getSubtotalSeriesData(encoding, series));
}

function makeSeriesDataImpl(
  model: RenderDataModel,
  schema: MuzeDataSchema,
  encoding: Encoding,
  dataProviderFn: (series: string) => Readonly<TotalSeriesData>
): TotalMuzeData {
  const discretes = schema.filter((sf: MuzeDataField) => sf.as === 'discrete' && sf.name !== SID_FIELD);

  // Include the measures on the series
  const measures: Array<MuzeDataField> = encoding.marks.series
    .map((series) => schema.find((sf: MuzeDataField) => sf.name === series.field))
    .filter((sf): sf is MuzeDataField => sf !== undefined);

  const seriesTotals: TotalSeriesData[] = [];
  const measureNameById: Record<string, string> = {};

  measures.forEach((measure) => {
    const seriesData = dataProviderFn(measure.name);
    seriesTotals.push(seriesData);
    const compiledField = model.compileEncField(EncodingHelpers.getEncFieldById(encoding, measure.name));
    measureNameById[measure.name] = compiledField && compiledField.isValid ? compiledField.displayName : measure.name;
  });
  const markSummary = EncodingHelpers.getSummary(encoding);
  const seriesMarkSummary = measures.reduce((memo, meas, index): SeriesMarkSummaryMap => {
    const series: Readonly<EncSeries> = encoding.marks.series[index];
    if (series.field !== meas.name) {
      throw new Error(
        `measure -> series mismatch on series at index ${index}. series: "${series.field}" meas: "${meas.name}"`
      );
    }
    const { unique, shared } = getSeriesMarksSummaryAt(encoding.marks.series, markSummary, index);
    return {
      ...memo,
      [series.field]: {
        unique,
        shared,
      },
    };
  }, {});

  return makeCombinedTotals(measureNameById, discretes, seriesTotals, seriesMarkSummary);
}

function makeTabularTotals(totalData: TotalSeriesData, schema: MuzeDataSchema, isSubtotals = false): TotalMuzeData {
  totalData = !isSubtotals ? patchTotalsAllData(totalData, schema) : totalData;
  const result: TotalMuzeData = {};

  if (totalData.col) {
    // Corner data only gets added to the column totals
    // https://mode.slack.com/archives/C02GA7Z3X8Q/p1641390870013900?thread_ts=1641334173.011200&cid=C02GA7Z3X8Q
    result.col = buildRowOrColumnTotals(totalData.col, schema, totalData.all);
  }
  if (totalData.row) {
    result.row = buildRowOrColumnTotals(totalData.row, schema, []);
  }

  return result;
}

export function makeTotalsFormatter(
  model: RenderDataModel,
  encoding: Encoding,
  fieldFormatMap: ChartTypes.FieldFormatMap
): MuzeDataFormatFn | undefined {
  const measureNamesField = EncodingHelpers.findInEncoding(encoding, Names.Key);
  if (measureNamesField) {
    const measureNamesFormatter = buildMuzeMeasureNamesTextFormatter(
      buildMeasureNamesFieldMap(model, encoding.value, fieldFormatMap)
    );
    return function textFormatter(dataInfo: MuzeTextDataInfo, contextInfo: MuzeContextInfo) {
      if (
        dataInfo.rawValue === TOTALS_TOKEN ||
        (Array.isArray(dataInfo.rawValue) && dataInfo.rawValue[0] === TOTALS_TOKEN)
      ) {
        return TOTAL_LABEL;
      }
      return measureNamesFormatter(dataInfo, contextInfo);
    };
  }
}

/**
 * Determines the totals split fields to use for the Muze configuration. This is required
 * when displaying multiple totals rows and/or columns as is the case for tables with
 * Measure Names and Measure Values. The split field is used by Muze to determine the
 * appropriate label (i.e the measure name) to show with the total value.
 * @param encoding Encoding
 * @returns an object containing the 'row' and 'column' split EncField or undefined if there
 * isn't one.
 */
export function getTotalsSplitFields(encoding: Encoding) {
  const findFirstNamesEncField = (fields: ReadonlyArray<EncField>) => {
    return fields.find((field) => field && field.formula === Names.Key);
  };

  const splitFields: { row?: EncField; column?: EncField } = {};
  if (encoding.totals?.grand.row) {
    splitFields.row = findFirstNamesEncField([...encoding.column, ...encoding.x]);
  }
  if (encoding.totals?.grand.column) {
    splitFields.column = findFirstNamesEncField([...encoding.row, ...encoding.y]);
  }
  return splitFields;
}

/**
 * @description Fills in the `all` data, if necessary, with an empty
 * QueryResult.Item such that we render a corner grand total cell.
 * ---
 *
 * This is a valid scenario when we have an encoding of the following form:
 *
 * - One or more discrete field in Columns
 * - One or more discrete field in Rows
 * - 0 continuous fields in a visual channel
 * - Row and col grand totals have been enabled
 */
function patchTotalsAllData(totalData: TotalSeriesData, schema: MuzeDataSchema) {
  if (totalData.row != null && totalData.col != null && totalData.all == null) {
    const discrete = schema.filter((schema: MuzeDataField) => schema.as === 'discrete');
    const continuous = schema.filter((schema: MuzeDataField) => schema.as === 'continuous');

    // Sanity check. This "shouldn't" happen (famous last words).
    // In the event that it does, it'd be helpful to be able to
    // see it in the console. This is subject to change if the
    // conditions around GT queries change, which is why it's not
    // a hard error
    if (discrete.length === 0 || continuous.length !== 0) {
      console.warn(
        `patchTotalsAllData: Invalid totals config detected. Expected 1 or more discrete fields ` +
          `and 0 continuous fields: saw ${discrete.length} and ${continuous.length}, respectively`
      );
    }

    totalData = {
      ...totalData,
      // Inserting an empty QueryResult.Item here so when we
      // build the totals for Muze we create a corner cell.
      // Fields that exist in the schema will have their name
      // mapped in to the MuzeDataItem with a value of TOTALS_TOKEN,
      // which is what Muze requires for that cell to be rendered
      all: [{}],
    };
  }

  return totalData;
}

function buildRowOrColumnTotals(
  rowColData: readonly QueryResult.Item[],
  schema: MuzeDataSchema,
  allData?: readonly QueryResult.Item[]
): MuzeDataset {
  const data: QueryResult.Item[] = [];

  const dataset = allData?.length ? [...rowColData, ...allData] : rowColData;

  for (const record of dataset) {
    const item: MuzeDataItem = {};

    for (const field of schema) {
      if (hasOwnProp.call(record, field.name)) {
        item[field.name] = record[field.name];
      } else if (field.source === Attr.Source.Names) {
        item[field.name] = record[Names.Key];
      } else {
        item[field.name] = TOTALS_TOKEN;
      }
    }
    data.push(item);
  }

  return data;
}

/**
 *
 * Creates a shared dataset that can be loaded into Muze
 *
 * This method works by combining the dataset columns into a single schema
 * and appending the seriesTotals together.
 *
 * @param measureNameById - The record containing the measure name mapped to display name
 * @param discreteFields - The set of discrete fields associated with each dataset
 * @param seriesTotals - The seriesTotals returned by the underlying model
 */
function makeCombinedTotals(
  measureNameById: Record<string, string>,
  discreteFields: MuzeDataField[],
  seriesTotals: TotalSeriesData[],
  seriesMarkSummary: SeriesMarkSummaryMap
): TotalMuzeData {
  const result: TotalMuzeData = {};

  if (seriesTotals.length) {
    result.col = buildRowOrColumnSeriesTotals(seriesTotals, measureNameById, discreteFields, seriesMarkSummary, 'col');
    result.row = buildRowOrColumnSeriesTotals(seriesTotals, measureNameById, discreteFields, seriesMarkSummary, 'row');
  }

  return result;
}

export function buildRowOrColumnSeriesTotals(
  seriesTotals: TotalSeriesData[],
  measureNameById: Record<string, string>,
  discreteFields: MuzeDataField[],
  seriesMarkSummary: SeriesMarkSummaryMap,
  direction: 'row' | 'col'
): MuzeDataset | undefined {
  if (seriesTotals.length === 0) {
    return;
  }

  const data: MuzeDataItem[] = [];

  // Pre-combine the `dir` + all totals for each series
  const combinedSeriesTotals = seriesTotals.map((totals) => ({
    combinedTotals: [...(totals[direction] ?? []), ...(totals.all ?? [])],
    series: totals.series,
  }));

  combinedSeriesTotals.forEach(({ series, combinedTotals }) => {
    const markRefs: Array<[from: string, to: string]> = [];
    const sMarksSummary = seriesMarkSummary[series];

    for (const markRef of sMarksSummary.shared) {
      if (markRef.id != null) {
        markRefs.push([markRef.id, markRef.referenceId ?? markRef.id]);
      }
    }

    combinedTotals.forEach((record) => {
      const item: MuzeDataItem = {
        [SID_FIELD]: series,
        ...record,
      };

      // Maps a unique/local series mark to a corresponding alias that exists in
      // the Muze Schema. Each shared mark will have a `referenceId` that maps a
      // series' mark to the alias we provided to Muze. If the `from` alias is not
      // in the result then it was omitted for GT, leave it uncopied so we pick it
      // up when assigning TOTALS_TOKEN
      markRefs.forEach(([from, to]) => {
        if (hasOwnProp.call(item, from)) {
          item[to] = item[from];
        }
      });

      // If a discrete field doesn't exist in the record at this point then
      // we've found one of the grand total discrete fields; set the token
      discreteFields.forEach(({ name, source }) => {
        if (source === Attr.Source.Names) {
          item[name] = item[Names.Key];
          if (item[name] === 'No Measure Value') {
            item[name] = measureNameById[series] ?? item[name];
          }
        }
        if (!hasOwnProp.call(item, name)) {
          item[name] = TOTALS_TOKEN;
        }
      });

      data.push(item);
    });
  });

  return data.length ? data : undefined;
}
